import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  AccessTime as TimeIcon,
  TableRestaurant as TableIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as ConfirmIcon,
  EventSeat as SeatIcon,
  Done as CompleteIcon,
  Cancel as CancelIcon,
  DoNotDisturb as NoShowIcon,
  Edit as EditIcon,
  Notes as NotesIcon,
  Source as SourceIcon,
} from '@mui/icons-material';
import Modal from '../Modal';
import { useReservations } from '../../context/ReservationContext';
import { useConfirm } from '../../context/ConfirmContext';
import type { Reservation, ReservationStatus } from '../../types/reservation.types';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';

interface ReservationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  onEdit: (reservation: Reservation) => void;
}

const STATUS_CONFIG: Record<ReservationStatus, { label: string; color: string; bg: string }> = {
  PENDING: { label: 'Pending', color: 'var(--status-warn-text)', bg: '#fef3c7' },
  CONFIRMED: { label: 'Confirmed', color: 'var(--text-link)', bg: '#dbeafe' },
  SEATED: { label: 'Seated', color: 'var(--status-ok-text)', bg: '#d1fae5' },
  COMPLETED: { label: 'Completed', color: 'var(--text-secondary)', bg: '#f1f5f9' },
  CANCELLED: { label: 'Cancelled', color: 'var(--status-bad)', bg: '#fee2e2' },
  NO_SHOW: { label: 'No Show', color: 'var(--status-bad-text)', bg: '#fee2e2' },
};

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color: string;
  bg: string;
  hoverBg: string;
  disabled?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, onClick, color, bg, hoverBg, disabled }) => (
  <Box
    component="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      px: 2,
      py: 1,
      borderRadius: 2,
      border: 'none',
      bgcolor: bg,
      color,
      fontWeight: 600,
      fontSize: '0.8125rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s',
      '&:hover:not(:disabled)': { bgcolor: hoverBg },
      fontFamily: 'inherit',
      minHeight: 44,
      flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
    }}
  >
    {icon}
    {label}
  </Box>
);

const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  isOpen,
  onClose,
  reservation,
  onEdit,
}) => {
  const {
    confirmReservation,
    seatReservation,
    completeReservation,
    cancelReservation,
    markNoShow,
  } = useReservations();
  const { theme } = useTheme();
  const confirm = useConfirm();

  const [loading, setLoading] = useState<string | null>(null);

  if (!reservation) return null;

  const statusConfig = STATUS_CONFIG[reservation.status] || STATUS_CONFIG.PENDING;

  const handleAction = async (action: string, fn: (id: number) => Promise<void>, successMsg: string) => {
    setLoading(action);
    try {
      await fn(reservation.id);
      toast.success(successMsg);
      onClose();
    } catch {
      toast.error('Action failed');
    } finally {
      setLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const tableName = reservation.tableGroupName || reservation.tableName || 'Unassigned';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" maxWidth="sm">
      <Box>
        {/* Header with status */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)', mb: 0.5 }}>
              {reservation.customerName}
            </Typography>
            <Box
              component="span"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            >
              {statusConfig.label}
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
            #{reservation.reservationCode}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Details grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
          {/* Phone */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PhoneIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Phone</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{reservation.customerPhone}</Typography>
            </Box>
          </Box>

          {/* Party Size */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PeopleIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Party Size</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{reservation.partySize} guests</Typography>
            </Box>
          </Box>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CalendarIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(reservation.reservationDate)}</Typography>
            </Box>
          </Box>

          {/* Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TimeIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Time</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
              </Typography>
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <TableIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Table</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{tableName}</Typography>
            </Box>
          </Box>

          {/* Source */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <SourceIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', lineHeight: 1 }}>Source</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                {reservation.source.toLowerCase()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Email if present */}
        {reservation.customerEmail && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <EmailIcon sx={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{reservation.customerEmail}</Typography>
          </Box>
        )}

        {/* Special Occasion Tag */}
        {reservation.specialOccasionTag && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 2, fontSize: '0.75rem', fontWeight: 600,
              bgcolor: `${theme.primary}18`,
              color: theme.primary,
            }}>
              {reservation.specialOccasionTag.replace(/_/g, ' ')}
            </Box>
          </Box>
        )}

        {/* Guest Notes */}
        {reservation.guestNotes && (
          <Box sx={{ mb: 2, p: { xs: 1, sm: 1.5 }, borderRadius: 2, bgcolor: `${theme.secondary}15`, border: 1, borderColor: `${theme.secondary}40` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <NotesIcon sx={{ fontSize: 14, color: theme.secondary }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: theme.secondary }}>Guest Notes</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: theme.text, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {reservation.guestNotes}
            </Typography>
          </Box>
        )}

        {/* Special Requests */}
        {reservation.specialRequests && (
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'var(--status-warn-bg)', border: 1, borderColor: 'var(--status-warn-border)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <NotesIcon sx={{ fontSize: 14, color: 'var(--status-warn-text)' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--status-warn-text)' }}>Special Requests</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--status-warn-text)' }}>
              {reservation.specialRequests}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action buttons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {reservation.status === 'PENDING' && (
            <ActionButton
              icon={loading === 'confirm' ? <CircularProgress size={14} /> : <ConfirmIcon sx={{ fontSize: 16 }} />}
              label="Confirm"
              onClick={() => handleAction('confirm', confirmReservation, 'Reservation confirmed')}
              color="#1d4ed8"
              bg="#dbeafe"
              hoverBg="#bfdbfe"
              disabled={loading !== null}
            />
          )}
          {(reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') && (
            <ActionButton
              icon={loading === 'seat' ? <CircularProgress size={14} /> : <SeatIcon sx={{ fontSize: 16 }} />}
              label="Seat"
              onClick={() => handleAction('seat', seatReservation, 'Customer seated')}
              color="#065f46"
              bg="#d1fae5"
              hoverBg="#a7f3d0"
              disabled={loading !== null}
            />
          )}
          {reservation.status === 'SEATED' && (
            <ActionButton
              icon={loading === 'complete' ? <CircularProgress size={14} /> : <CompleteIcon sx={{ fontSize: 16 }} />}
              label="Complete"
              onClick={() => handleAction('complete', completeReservation, 'Reservation completed')}
              color="#475569"
              bg="#f1f5f9"
              hoverBg="#e2e8f0"
              disabled={loading !== null}
            />
          )}
          {(reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') && (
            <>
              <ActionButton
                icon={loading === 'cancel' ? <CircularProgress size={14} /> : <CancelIcon sx={{ fontSize: 16 }} />}
                label="Cancel"
                onClick={async () => {
                  if (await confirm({
                    title: 'Cancel this reservation?',
                    description: `${reservation.customerName} · party of ${reservation.partySize} · ${reservation.startTime}. This frees the table and can't be undone.`,
                    confirmText: 'Cancel reservation',
                    cancelText: 'Keep it',
                  })) handleAction('cancel', cancelReservation, 'Reservation cancelled');
                }}
                color="#dc2626"
                bg="#fee2e2"
                hoverBg="#fecaca"
                disabled={loading !== null}
              />
              <ActionButton
                icon={loading === 'noshow' ? <CircularProgress size={14} /> : <NoShowIcon sx={{ fontSize: 16 }} />}
                label="No-Show"
                onClick={async () => {
                  if (await confirm({
                    title: 'Mark as no-show?',
                    description: `${reservation.customerName} · party of ${reservation.partySize}. This frees the table and records a no-show.`,
                    confirmText: 'Mark no-show',
                    cancelText: 'Back',
                  })) handleAction('noshow', markNoShow, 'Marked as no-show');
                }}
                color="#991b1b"
                bg="#fee2e2"
                hoverBg="#fecaca"
                disabled={loading !== null}
              />
            </>
          )}

          {/* Edit always available for active reservations */}
          {(reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') && (
            <ActionButton
              icon={<EditIcon sx={{ fontSize: 16 }} />}
              label="Edit"
              onClick={() => { onClose(); onEdit(reservation); }}
              color="#6b7280"
              bg="#f3f4f6"
              hoverBg="#e5e7eb"
              disabled={loading !== null}
            />
          )}
        </Box>

        {/* Timestamps */}
        <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'var(--border-subtle)' }}>
          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block' }}>
            Created {new Date(reservation.createdAt).toLocaleString()}
            {reservation.confirmedAt && ` | Confirmed ${new Date(reservation.confirmedAt).toLocaleString()}`}
            {reservation.seatedAt && ` | Seated ${new Date(reservation.seatedAt).toLocaleString()}`}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReservationDetailModal;
